import { getFeatureFlag } from '@/services/ecs';

const httpStatus424 = 424;
/**
 * Retry a Promise-returning function on HTTP 424 status up to maxAttempts, waiting delayMs between retries.
 * Configuration is controlled via ECS feature flags: retryMaxAttempts and retryDelayMs
 * @param fn function that returns a Promise resolving to T
 * @param maxAttempts maximum number of attempts (defaults to ECS retryMaxAttempts, fallback: 3)
 * @param delayMs delay in milliseconds between retries (defaults to ECS retryDelayMs, fallback: 20000)
 */
export async function retryOnProvisioning<T>(
  fn: () => Promise<T>,
  maxAttempts?: number,
  delayMs?: number
): Promise<T> {
  // Use provided values, fall back to ECS feature flags, then hardcoded defaults
  const finalMaxAttempts = maxAttempts ?? getFeatureFlag('retryMaxAttempts', 3);
  const finalDelayMs = delayMs ?? getFeatureFlag('retryDelayMs', 20000);

  for (let attempt = 1; attempt <= finalMaxAttempts; attempt++) {
    try {
      return await fn();
    } catch (err: any) {
      const status = err?.status || err?.response?.status;
      if (status === httpStatus424 && attempt < finalMaxAttempts) {
        console.debug(`retryOnProvisioning: attempt ${attempt} failed with 424, retrying in ${finalDelayMs}ms`);
        await new Promise(res => setTimeout(res, finalDelayMs));
        continue;
      }
      throw err;
    }
  }
  // Should not reach here; throw a generic error
  throw new Error('retryOnProvisioning: max retries exceeded');
}